/**
 * Keycloak application-initiated actions (#998).
 *
 * The SPA never handles a password. To change one it asks Keycloak to run its
 * UPDATE_PASSWORD required action: an ordinary authorization request with
 * `kc_action=UPDATE_PASSWORD`. Keycloak shows its own form (asking for the
 * current password again if the sign-in is older than the action allows),
 * applies the realm's password policy, and redirects back to /auth/callback
 * with a code as usual plus `kc_action_status`: `success`, `cancelled` (the
 * user pressed Cancel) or `error`.
 */

export const UPDATE_PASSWORD = 'UPDATE_PASSWORD';

export type KcActionStatus = 'success' | 'cancelled' | 'error';

const STATUSES: readonly string[] = ['success', 'cancelled', 'error'] satisfies KcActionStatus[];

/** The `kc_action_status` Keycloak put on a callback URL's query, if any. */
export function kcActionStatus(search: string): KcActionStatus | null {
  const status = new URLSearchParams(search).get('kc_action_status');
  return status !== null && STATUSES.includes(status) ? (status as KcActionStatus) : null;
}

/** Router state AuthCallback hands to the page the action started from. */
export interface KcActionState {
  kcActionStatus?: KcActionStatus;
}

/** What to tell the user once Keycloak's password form sends them back. */
export function passwordChangeMessage(status: KcActionStatus): { type: 'success' | 'info' | 'error'; text: string } {
  switch (status) {
    case 'success':
      return { type: 'success', text: 'Password changed.' };
    case 'cancelled':
      return { type: 'info', text: 'Password not changed.' };
    case 'error':
      return { type: 'error', text: 'Your password could not be changed. Try again.' };
  }
}
